import { useState, useCallback } from 'react';
import { useLoading } from '../contexts/LoadingContext';

interface UseApiWithLoadingOptions {
  showGlobalLoading?: boolean;
  showLocalLoading?: boolean;
  autoStart?: boolean;
}

interface UseApiWithLoadingReturn<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  execute: (...args: any[]) => Promise<T>;
  reset: () => void;
}

export function useApiWithLoading<T = any>(
  apiFunction: (...args: any[]) => Promise<T>,
  options: UseApiWithLoadingOptions = {}
): UseApiWithLoadingReturn<T> {
  const {
    showGlobalLoading = true,
    showLocalLoading = true,
    autoStart = false,
  } = options;

  const { startLoading, stopLoading } = useLoading();
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(autoStart);
  const [error, setError] = useState<string | null>(null);

  const execute = useCallback(
    async (...args: any[]): Promise<T> => {
      try {
        // Start loading states
        if (showGlobalLoading) {
          startLoading();
        }
        if (showLocalLoading) {
          setLoading(true);
        }
        setError(null);

        // Execute API call
        const result = await apiFunction(...args);

        // Set data and stop loading
        setData(result);
        return result;
      } catch (err) {
        // Handle errors
        const errorMessage = err instanceof Error ? err.message : 'An error occurred';
        setError(errorMessage);
        throw err;
      } finally {
        // Stop loading states
        if (showGlobalLoading) {
          stopLoading();
        }
        if (showLocalLoading) {
          setLoading(false);
        }
      }
    },
    [apiFunction, showGlobalLoading, showLocalLoading, startLoading, stopLoading]
  );

  const reset = useCallback(() => {
    setData(null);
    setError(null);
    setLoading(false);
  }, []);

  return {
    data,
    loading,
    error,
    execute,
    reset,
  };
}

// Specialized hooks for common use cases
export function useApiWithGlobalLoading<T = any>(
  apiFunction: (...args: any[]) => Promise<T>
): UseApiWithLoadingReturn<T> {
  return useApiWithLoading(apiFunction, {
    showGlobalLoading: true,
    showLocalLoading: false,
  });
}

export function useApiWithLocalLoading<T = any>(
  apiFunction: (...args: any[]) => Promise<T>
): UseApiWithLoadingReturn<T> {
  return useApiWithLoading(apiFunction, {
    showGlobalLoading: false,
    showLocalLoading: true,
  });
}

export function useApiWithoutLoading<T = any>(
  apiFunction: (...args: any[]) => Promise<T>
): UseApiWithLoadingReturn<T> {
  return useApiWithLoading(apiFunction, {
    showGlobalLoading: false,
    showLocalLoading: false,
  });
}
